import {
  type EntityCollection,
  type EntityCollectionsBuilder,
} from "@firecms/core";
import { buildStoriesCollection, buildRelatedStoriesCollection } from "./stories";
import { buildLevelsCollection, buildRelatedLevelsCollection } from "./levels";
import {
  buildAssignedParticipantsCollection,
} from "./participants";
import { buildEventInvitationsCollection } from "./eventInvitations";
import { buildTeamsCollection } from "./teams";
import {
  buildEventUsersCollection,
  buildStandbyEventUsersCollection,
} from "./eventUsers";
import {
  buildAccountNotificationDefsCollection,
  buildEventNotificationsCollection,
} from "./notifications";
import { accountsCollection } from "./accounts";
import { deletedUsersCollection } from "./deletedUsers";
import { managersCollection } from "./managers";
import { buildInvitationsCollection } from "./invitations";
import { buildArchivedEventsCollection, buildEventsCollection } from "./events";
import { buildSpacesCollection } from "./spaces";
import { buildUnitCollection, buildRelatedUnitsCollection } from "./units";
import { buildQuestionsCollection } from "./questions";
import {
  canAccessMode,
  type AdminMode,
} from "../workspace/adminMode";
import { withLocalizedStringValidation } from "../localization";
import type { AuthExtras } from "../types/auth";
import type { Locale } from "../localization/constants";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import {
  fetchAccountUserMembership,
  loadManagedEventKeys,
} from "../firebase/membershipQueries";

export const collectionsBuilderCreator =
  (mode: AdminMode, locale: Locale = getAdminUiLocale()): EntityCollectionsBuilder =>
    async ({ authController, dataSource }) => {
      if (!canAccessMode(mode, authController.extra)) {
        return [];
      }

      const extra = authController.extra as AuthExtras | undefined;
      const collections: EntityCollection[] = [];

      switch (mode.kind) {
        case "hub":
          return [];

        case "global": {
          if (!extra?.isSuperManager) {
            return [];
          }
          collections.push(accountsCollection);
          collections.push(deletedUsersCollection);
          collections.push(managersCollection);
          break;
        }

        case "account": {
          const userId = authController.user?.uid;
          const isSuperManager = extra?.isSuperManager === true;
          const isAccountManagerForAccount =
            isSuperManager ||
            (userId
              ? await fetchAccountUserMembership(
                  dataSource,
                  userId,
                  mode.id.accountId
                )
              : false);
          const managedEventKeys =
            isSuperManager || isAccountManagerForAccount
              ? undefined
              : await loadManagedEventKeys(dataSource);

          collections.push(buildSpacesCollection(mode.id));
          const eventActionOptions = {
            isSuperManager,
            isAccountManagerForAccount,
            managedEventKeys,
          };

          collections.push(buildEventsCollection(mode.id, eventActionOptions, locale));

          collections.push(buildInvitationsCollection(mode.id));

          if (isAccountManagerForAccount) {
            collections.push(buildAccountNotificationDefsCollection(mode.id));
            collections.push(
              buildArchivedEventsCollection(mode.id, {
                ...eventActionOptions,
                archivedView: true,
              }, locale)
            );
          }

          break;
        }

        case "space": {
          const spaceId = mode.id;
          collections.push(buildUnitCollection(spaceId, locale));
          collections.push(buildStoriesCollection(spaceId));
          collections.push(buildQuestionsCollection(spaceId));
          collections.push(buildLevelsCollection(spaceId));
          break;
        }

        case "event": {
          const eventId = mode.id;

          collections.push(buildRelatedUnitsCollection(eventId));
          collections.push(buildRelatedStoriesCollection(eventId));
          collections.push(buildRelatedLevelsCollection(eventId));
          collections.push(buildStandbyEventUsersCollection(eventId));
          collections.push(buildEventUsersCollection(eventId));
          collections.push(buildAssignedParticipantsCollection(eventId, undefined, locale));
          collections.push(buildEventInvitationsCollection(eventId, undefined, locale));
          collections.push(buildTeamsCollection(eventId));
          collections.push(buildEventNotificationsCollection(eventId));
          break;
        }
      }

      return collections.map(withLocalizedStringValidation);
    };
